<!--
  The gallery is constructed from an input array of images
  and presents them sequentially to the user.
  - Navigation through the gallery is facilitated through keyboard arrow keys
    or on-screen navigation buttons
  - Zoom functionality, allowing users to magnify images for detail.
  - Fullscreen toggle, enabling a more immersive viewing experience.
 -->
<script>
export default {
  name: 'PhotoGallery2',
}
</script>

<script setup>
import {useMagicKeys, useSwipe, useFullscreen} from '@vueuse/core'
import {Loading as VanLoading} from 'vant'
import {ref, computed, watch, nextTick} from 'vue'
import {useApplicationStore} from '@stores/application.store'

const {mobile} = useApplicationStore()
const {arrowleft, arrowright, escape} = useMagicKeys()

const props = defineProps({
  // images
  images: {
    type: Array,
    required: true,
    default: () => [],
  },
  // target
  // - target
  target: {
    type: String,
    required: false,
    default: null,
  },
  // visible
  // - v-model
  visible: {
    type: Boolean,
    required: true,
    default: false,
  },
  // dataFormat2
  // - indicates media array consists of object items with type info
  dataFormat2: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emits = defineEmits([
  'update:visible',
])

// current
const current = ref(null)
// currentImageIndex
const currentImageIndex = ref(null)
// imageWrapper
const imageWrapper = ref(null)
// enableZoomView
const enableZoomView = ref(false)
// zoomLevel
const zoomLevel = ref(2)
// zoomMouseX
const zoomMouseX = ref(0)
// zoomMouseY
const zoomMouseY = ref(0)
// enableFullscreen
const enableFullscreen = ref(false)
// fullscreenEl
const fullscreenEl = ref(null)
// fullscreen
const fullscreen = useFullscreen(fullscreenEl)
// lengthOfImage

// toolbar
const toolbar = ref(null)
// toolbarHeight
const toolbarHeight = ref(null)

// mediaItems
// - 2025.05.28
// - Normalize input to an array of unified objects
const mediaItems = computed(() => {
  if (props.dataFormat2) {
    // Array already contains meta objects
    return props.images.map(item => ({
      src: item.src || '',
      remark: item.remark || 'image',
      video_cover: item.video_cover || null,
    }))
  }

  // Legacy mode: array of strings
  return props.images.map(src => ({
    src,
    remark: 'image',
    video_cover: null,
  }))
})

const lengthOfImages = computed(() => mediaItems.value.length)

// visible
watch(() => props.visible, async isVisible => {
  if (isVisible) {
    // normalize target src when dataFormat2 is on
    const targetSrc = props.target

    current.value = targetSrc
      ? targetSrc
      : mediaItems.value[0]?.src

    currentImageIndex.value = mediaItems.value.findIndex(item => item.src === current.value)

    await nextTick()
    toolbarHeight.value = toolbar.value?.clientHeight || 0
  }
})

// mediaIsReady
const mediaIsReady = ref(false)

// currentImageIndex
watch(() => currentImageIndex.value, (index) => {
  // pause any playing videos when leaving slide
  try {
    const videos = imageWrapper.value?.querySelectorAll('video')

    videos?.forEach(v => v.pause())
  }
  catch (err) {
    console.error(err)
    /* swallow */
  }

  mediaIsReady.value = false
  const item = mediaItems.value[index]

  // console.log('[Gallery] switched to', item)

  if (!item) return

  if (item.remark === 'image') {
    const img = new Image()

    img.onload = () => mediaIsReady.value = true
    img.src = item.src
  }
  else {
    // video slides show immediately; skip spinner
    mediaIsReady.value = true
  }
}, {
  immediate: false,
})

// isCurrentVideo
const isCurrentVideo = computed(() => mediaItems.value[currentImageIndex.value]?.remark === 'video')

// watch(key: escape)
// - 2023.08.03
// - key 'escape' is pressed
watch(escape, v => {
  props.visible && v && emits('update:visible', false)
})

// prev
// - 2023.08.04
// - Navigate to the previous photo,
const prev = () => {
  if (currentImageIndex.value > 0) {
    enableZoomView.value = false
    currentImageIndex.value--
  }
}

// next
// - 2023.08.04
// - Navigate to the next photo
const next = () => {
  if (currentImageIndex.value < lengthOfImages.value - 1) {
    enableZoomView.value = false
    currentImageIndex.value++
  }
}

// watch(key: arrowleft)
// - 2023.08.03
// - key 'arrow left' is pressed,
//   only perform `prev()` when photo-gallery
//   is current actively open
watch(arrowleft, v => {
  props.visible && v && prev()
})

// watch(key: arrowright)
// - 2023.08.03
// - key 'arrow right' is pressed
//   only perform `next()` when photo-gallery
//   is current actively open
watch(arrowright, v => {
  props.visible && v && next()
})

// closeBtnDidPress
// - 2023.08.03
const closeBtnDidPress = () => {
  enableZoomView.value = false
  enableFullscreen.value = false
  emits('update:visible', false)
}

// Enable swipe on mobile device
// - 2023.08.04
useSwipe(imageWrapper, {
  passive: false,
  onSwipeEnd (e, direction) {
    if (direction === 'left') {
      next()
    }
    else if (direction === 'right') {
      prev()
    }
  },
})

// mouseDidMove
const mouseDidMove = (e) => {
  const target = e.currentTarget
  const offsetX = e.offsetX ? e.offsetX : e.touches[0].pageX
  const offsetY = e.offsetY ? e.offsetY : e.touches[0].pageY

  zoomMouseX.value = offsetX / target.offsetWidth * 100
  zoomMouseY.value = offsetY / target.offsetHeight * 100
}
</script>

<template>
  <Teleport to="body">
    <template v-if="visible">
      <div
        ref="fullscreenEl"
        class="image-gallery-2-wrapper">
        <div class="image-gallery-2-container">
          <!-- toolbar -->
          <div
            ref="toolbar"
            class="image-gallery-2__toolbar">
            <!-- count indicator -->
            <div class="image-gallery-2__count-indicator">
              {{ currentImageIndex + 1 }}  / {{ lengthOfImages }}
            </div>
            <!--/ count indicator -->

            <!-- menu -->
            <div class="toolbar-menu">
              <!-- zoom btn -->
              <template v-if="!mobile && !isCurrentVideo">
                <div class="btn-wrapper">
                  <template v-if="enableZoomView">
                    <font-awesome-icon
                      :icon="'search-minus'"
                      :class="['btn-zoom-out']"
                      :title="'还原'"
                      @click.prevent="enableZoomView = !enableZoomView" />
                  </template>
                  <template v-else>
                    <font-awesome-icon
                      :icon="'search-plus'"
                      :class="'btn-zoom-in'"
                      :title="'放大'"
                      @click.prevent="enableZoomView = !enableZoomView" />
                  </template>
                </div>
              </template>
              <!--/ zoom btn -->

              <!-- fullscreen  btn-->
              <template v-if="!mobile">
                <div class="btn-wrapper">
                  <template v-if="enableFullscreen">
                    <font-awesome-icon
                      :icon="'compress'"
                      :class="['btn-disable-fullscreen']"
                      :title="'退出全屏'"
                      @click.prevent="() => {
                        fullscreen.toggle()
                        enableFullscreen = !enableFullscreen
                      }" />
                  </template>
                  <template v-else>
                    <font-awesome-icon
                      :icon="'expand'"
                      :class="['btn-enable-fullscreen']"
                      :title="'进入全屏'"
                      @click.prevent="() => {
                        fullscreen.toggle()
                        enableFullscreen = !enableFullscreen
                      }" />
                  </template>
                </div>
              </template>
              <!--/ fullscreen btn -->

              <!-- close btn-->
              <div class="btn-wrapper">
                <font-awesome-icon
                  :class="'btn-close'"
                  :size="'xl'"
                  :icon="'times'"
                  :title="'关闭'"
                  @click="closeBtnDidPress" />
              </div>
              <!--/ close btn-->
            </div>
            <!--/ menu -->
          </div>
          <!--/ toolbar -->

          <!-- btn prev wrapper -->
          <template v-if="!mobile && lengthOfImages > 1">
            <div
              :class="['btn-prev-wrapper']"
              @click="prev">
              <font-awesome-icon
                :class="['btn-prev']"
                :icon="'angle-left'" />
            </div>
          </template>
          <!--/ btn prev wrapper -->

          <!-- btn next wrapper -->
          <template v-if="!mobile && lengthOfImages > 1">
            <div
              :class="['btn-next-wrapper']"
              @click="next">
              <font-awesome-icon
                :class="['btn-next']"
                :icon="'angle-right'" />
            </div>
          </template>
          <!--/ btn next wrapper -->

          <div
            ref="imageWrapper"
            class="photo-gallery-2__images">
            <template
              v-for="(media, mediaIndex) in mediaItems"
              :key="mediaIndex">
              <Transition :name="'fade'">
                <template v-if="mediaIndex === currentImageIndex && mediaIsReady">
                  <!-- image slide -->
                  <template v-if="media.remark === 'image'">
                    <figure
                      class="image-zoom-holder"
                      :style="{
                        backgroundPosition: enableZoomView
                          ? `${zoomMouseX}% ${zoomMouseY}%`
                          : null,
                        backgroundImage: `url(${media.src}!/fw/2800/rotate/auto/format/webp)`,
                        backgroundSize: enableZoomView ? `${zoomLevel * 100}%` : '0px',
                        height: `calc(100% - ${toolbarHeight}px)`
                      }"
                      @mousemove="e => enableZoomView ? mouseDidMove(e) : null ">
                      <img
                        :class="['image-el']"
                        :src="`${media.src}!/fw/2800/rotate/auto/format/webp`"
                        :alt="''"
                        :style="{
                          opacity: enableZoomView ? 0 : 1
                        }">
                    </figure>
                  </template>
                  <!-- video slide -->
                  <template v-else-if="media.remark === 'video'">
                    <div
                      class="gallery-video-wrapper"
                      :style="{ height: `calc(100% - ${toolbarHeight}px)` }">
                      <video
                        class="gallery-video"
                        controls
                        :src="media.src"
                        :poster="media.video_cover"
                        @play="console.log('[Gallery] video play', media.src)"
                        @pause="console.log('[Gallery] video pause', media.src)" />
                    </div>
                  </template>
                </template>
                <template v-else>
                  <van-loading
                    v-show="!mediaIsReady"
                    :size="60"
                    :color="'#ffffff'"
                    class="loading-indicator" />
                </template>
              </Transition>
            </template>
          </div>
        </div>
      </div>
    </template>
  </Teleport>
</template>

<style lang="scss" scoped>
.image-gallery-2-wrapper {
  background: rgba(0, 0, 0, 0.8);
  z-index: 100;
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.image-gallery-2-container {
  width: 100%;
  height: 100%;
  position: relative;
  margin: 0 auto;
}

.image-gallery-2__toolbar {
  padding: 5px 10px;
  box-sizing: border-box;
  width: 100%;
  position: relative;
  z-index: 300;
  background: rgba(0,0,0, .8);
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}

.image-gallery-2__count-indicator {
  color: #ffffff;
}

.toolbar-menu {
  margin-left: auto;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}

.toolbar-menu .btn-wrapper {
  margin: 0 10px;
}

.toolbar-menu .btn-close,
.toolbar-menu .btn-enable-fullscreen,
.toolbar-menu .btn-disable-fullscreen,
.toolbar-menu .btn-zoom-in,
.toolbar-menu .btn-zoom-out {
  color: #ffffff;
  cursor: pointer;
}

.btn-prev-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  color: #ffffff;
  font-size: 50px;
  width: 100px;
  height: 100vh;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: .3s;
  &:hover {
    background-color: rgba(0,0,0,.1);
  }
}

.btn-next-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 200;
  color: #ffffff;
  font-size: 50px;
  width: 100px;
  height: 100vh;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: .3s;
  &:hover {
    background-color: rgba(0,0,0,.1);
  }
}

.btn-prev {
  z-index: 200;
  color: #ffffff;
  font-size: 50px;
  cursor: pointer;
}

.btn-next {
  z-index: 200;
  color: #ffffff;
  font-size: 50px;
  cursor: pointer;
}

.image-el {
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: contain;
  transition: opacity .2s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loading-indicator {
  color: #ffffff;
  position: absolute;
  left: calc(50% - 15px);
  top: calc(50% - 15px);
}

.image-zoom-holder {
  width: 100%;
  height: 100%;
  position: absolute;
  display: block;
  transition: background 0s ease;
  will-change: auto;
}

.gallery-video-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  outline: none;
}
</style>
